// /invoices — facturación electrónica (Facturante / ARCA).
//
// Listado con filtros + detalle lateral. Las dos acciones de reparación viven
// acá para que un problema de facturación se resuelva sin salir del backoffice:
// reintentar la emisión y reenviar el mail al usuario.
//
// El detalle consulta el estado VIVO en Facturante cuando la factura no está
// resuelta. Ese contraste es el que decide: si el comprobante ya existe del
// otro lado, reintentar no corresponde (lo cierra el reconciler diario).
//
// `support` entra a la pantalla en modo lectura: busca la factura y abre el
// PDF, que es lo que le piden por teléfono. Las dos acciones de reparación son
// de `admin` — el backend las rechaza igual, esto solo evita ofrecerlas.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listInvoices,
  getInvoice,
  retryInvoice,
  resendInvoiceEmail,
  listPartners,
  AdminApiError,
  type AdminInvoice,
  type InvoiceStatus,
} from '@/lib/api';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';
import { useSession } from '@/hooks/useSession';
import { canWrite } from '@/lib/access';

const STATUS_BADGE: Record<InvoiceStatus, { label: string; cls: string }> = {
  authorized: {
    label: 'autorizada',
    cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  submitted: {
    label: 'en trámite',
    cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  },
  pending: {
    label: 'pendiente',
    cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  },
  error: {
    label: 'error',
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  rejected: {
    label: 'rechazada',
    cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  },
};

const PAGE_SIZE = 50;

function money(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(amount);
}

export default function Invoices(): JSX.Element {
  const queryClient = useQueryClient();

  const [fltPartner, setFltPartner] = useState('');
  const [fltStatus, setFltStatus] = useState('');
  const [fltSearch, setFltSearch] = useState('');
  const [page, setPage] = useState(0);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toRetry, setToRetry] = useState<AdminInvoice | null>(null);
  const [resendFor, setResendFor] = useState<AdminInvoice | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const session = useSession();
  const canRepair = canWrite(session, 'invoices');

  const partnersQ = useQuery({ queryKey: ['admin-partners'], queryFn: listPartners });

  const listQ = useQuery({
    queryKey: ['admin-invoices', fltPartner, fltStatus, fltSearch, page],
    queryFn: () =>
      listInvoices({
        ...(fltPartner ? { partner_id: fltPartner } : {}),
        ...(fltStatus ? { status: fltStatus as InvoiceStatus } : {}),
        ...(fltSearch.trim() ? { search: fltSearch.trim() } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const detailQ = useQuery({
    queryKey: ['admin-invoice', selectedId],
    queryFn: () => getInvoice(selectedId!),
    enabled: selectedId !== null,
  });

  const retryM = useMutation({
    mutationFn: (invoiceId: string) => retryInvoice(invoiceId),
    onSuccess: () => {
      setToRetry(null);
      setActionErr(null);
      setActionMsg('Reintento encolado. La factura se actualiza en unos segundos.');
      void queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-invoice'] });
    },
    onError: (err) => {
      setToRetry(null);
      setActionMsg(null);
      setActionErr(err instanceof AdminApiError ? err.message : 'No se pudo reintentar.');
    },
  });

  const resendM = useMutation({
    mutationFn: (args: { invoiceId: string; email?: string }) =>
      resendInvoiceEmail(args.invoiceId, args.email),
    onSuccess: () => {
      setResendFor(null);
      setResendEmail('');
      setActionErr(null);
      setActionMsg('Factura reenviada por email.');
    },
    onError: (err) => {
      setActionMsg(null);
      setActionErr(err instanceof AdminApiError ? err.message : 'No se pudo reenviar.');
    },
  });

  // Reset de página cuando cambia un filtro: quedarse en la página 4 de un
  // resultado que ahora tiene una sola es una pantalla vacía sin explicación.
  function changeFilter(fn: () => void): void {
    fn();
    setPage(0);
  }

  const total = listQ.data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-6">
      <header>
        <h1 data-help-page="invoices.page" data-help="invoices.page" className="text-xl font-semibold text-ink dark:text-ink-inverted">
          Facturación electrónica
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          Comprobantes emitidos ante ARCA por cada cobro. La emisión es automática; acá se
          revisa el estado y se reparan los casos que quedaron a medias.
        </p>
      </header>

      {actionMsg ? (
        <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
          {actionMsg}
        </p>
      ) : null}
      {actionErr ? (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {actionErr}
        </p>
      ) : null}

      <section className="rounded-xl border border-surface-border dark:border-surface-dark-border">
        <div
          data-help="invoices.filters"
          className="grid gap-3 border-b border-surface-border p-3 sm:grid-cols-3 dark:border-surface-dark-border"
        >
          <Field label="Partner">
            <select
              value={fltPartner}
              onChange={(e) => changeFilter(() => setFltPartner(e.target.value))}
              className={fieldCls()}
            >
              <option value="">Todos</option>
              {(partnersQ.data ?? []).map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado">
            <select
              value={fltStatus}
              onChange={(e) => changeFilter(() => setFltStatus(e.target.value))}
              className={fieldCls()}
            >
              <option value="">Todos</option>
              <option value="authorized">Autorizadas</option>
              <option value="submitted">En trámite</option>
              <option value="pending">Pendientes</option>
              <option value="error">Con error</option>
              <option value="rejected">Rechazadas</option>
            </select>
          </Field>
          <Field label="Número o CAE exacto">
            <input
              type="text"
              value={fltSearch}
              onChange={(e) => changeFilter(() => setFltSearch(e.target.value))}
              placeholder="FB 00002-00000001"
              className={fieldCls({ mono: true })}
            />
          </Field>
        </div>

        {listQ.isLoading ? <p className="p-4 text-sm text-ink-muted">Cargando…</p> : null}
        {listQ.isError ? (
          <p className="p-4 text-sm text-red-600">
            {listQ.error instanceof AdminApiError ? listQ.error.message : 'No se pudo cargar.'}
          </p>
        ) : null}

        {listQ.data ? (
          <div className="overflow-x-auto">
            <table data-help="invoices.table" className="w-full min-w-[56rem] text-sm">
              <thead>
                <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted dark:border-surface-dark-border">
                  <th className="p-2">Comprobante</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Partner</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">IVA</th>
                  <th className="p-2">Período</th>
                  <th className="p-2">Emitida</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {listQ.data.invoices.map((inv) => (
                  <tr
                    key={inv.invoice_id}
                    className="border-b border-surface-border/50 dark:border-surface-dark-border/50"
                  >
                    <td className="p-2 font-mono text-ink dark:text-ink-inverted">
                      {inv.full_number ?? <span className="text-ink-muted">sin emitir</span>}
                    </td>
                    <td className="p-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[inv.status].cls}`}
                      >
                        {STATUS_BADGE[inv.status].label}
                      </span>
                    </td>
                    <td className="p-2 text-ink-muted">{inv.partner_id}</td>
                    <td className="p-2 text-right">{money(inv.total_amount, inv.currency)}</td>
                    <td className="p-2 text-right text-ink-muted">
                      {money(inv.vat_amount, inv.currency)}
                    </td>
                    <td className="p-2 text-ink-muted">
                      {inv.service_period_start} → {inv.service_period_end}
                    </td>
                    <td className="p-2 text-ink-muted">{inv.created_at.slice(0, 10)}</td>
                    <td className="p-2 text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedId(inv.invoice_id)}
                        className="text-xs text-brand underline hover:opacity-80"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
                {listQ.data.invoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-ink-muted">
                      Sin facturas con esos filtros.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            <div className="flex items-center justify-between p-2 text-xs text-ink-muted">
              <span>
                {total} en total
                {total > 0
                  ? ` · mostrando ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)}`
                  : ''}
              </span>
              {maxPage > 0 ? (
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="rounded border border-surface-border px-2 py-1 disabled:opacity-40 dark:border-surface-dark-border"
                  >
                    Anterior
                  </button>
                  <span>
                    {page + 1} / {maxPage + 1}
                  </span>
                  <button
                    type="button"
                    disabled={page >= maxPage}
                    onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                    className="rounded border border-surface-border px-2 py-1 disabled:opacity-40 dark:border-surface-dark-border"
                  >
                    Siguiente
                  </button>
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      {selectedId ? (
        <section className="rounded-xl border border-surface-border p-4 dark:border-surface-dark-border">
          <div className="mb-3 flex items-start justify-between">
            <h2 className="text-lg font-semibold text-ink dark:text-ink-inverted">
              Detalle de la factura
            </h2>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="text-xs text-ink-muted underline hover:opacity-80"
            >
              Cerrar
            </button>
          </div>

          {detailQ.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
          {detailQ.isError ? (
            <p className="text-sm text-red-600">
              {detailQ.error instanceof AdminApiError
                ? detailQ.error.message
                : 'No se pudo cargar el detalle.'}
            </p>
          ) : null}

          {detailQ.data ? (
            <div className="space-y-4">
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <Row label="Comprobante" value={detailQ.data.invoice.full_number ?? '—'} mono />
                <Row label="CAE" value={detailQ.data.invoice.cae ?? '—'} mono />
                <Row
                  label="Vencimiento del CAE"
                  value={detailQ.data.invoice.cae_expires_at ?? '—'}
                />
                <Row
                  label="Neto / IVA / Total"
                  value={`${money(detailQ.data.invoice.net_amount, detailQ.data.invoice.currency)} · ${money(
                    detailQ.data.invoice.vat_amount,
                    detailQ.data.invoice.currency,
                  )} (${detailQ.data.invoice.vat_rate}%) · ${money(
                    detailQ.data.invoice.total_amount,
                    detailQ.data.invoice.currency,
                  )}`}
                />
                <Row label="Intentos" value={String(detailQ.data.invoice.attempts)} />
                <Row label="Pago" value={detailQ.data.invoice.payment_id} mono />
              </dl>

              {detailQ.data.invoice.error_message ? (
                <div className="rounded-lg bg-amber-50 p-3 text-sm dark:bg-amber-900/20">
                  <p className="font-medium text-amber-900 dark:text-amber-300">
                    Último error ({detailQ.data.invoice.error_code ?? 'sin código'})
                  </p>
                  <p className="mt-1 break-words text-amber-800 dark:text-amber-200">
                    {detailQ.data.invoice.error_message}
                  </p>
                </div>
              ) : null}

              {detailQ.data.provider_state ? (
                <div className="rounded-lg border border-surface-border p-3 text-sm dark:border-surface-dark-border">
                  <p className="mb-1 font-medium text-ink dark:text-ink-inverted">
                    Estado en Facturante ahora mismo
                  </p>
                  {detailQ.data.provider_state.lookup_error ? (
                    <p className="text-ink-muted">
                      No se pudo consultar: {detailQ.data.provider_state.lookup_error}
                    </p>
                  ) : detailQ.data.provider_state.found ? (
                    <p className="text-ink-muted">
                      El comprobante <strong>existe</strong> en Facturante (
                      {detailQ.data.provider_state.state}
                      {detailQ.data.provider_state.full_number
                        ? `, ${detailQ.data.provider_state.full_number}`
                        : ''}
                      ). No hace falta reintentar: la conciliación diaria lo cierra sola.
                    </p>
                  ) : (
                    <p className="text-ink-muted">
                      Facturante no tiene ningún comprobante para este cobro. Reintentar es
                      seguro.
                    </p>
                  )}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                {detailQ.data.invoice.pdf_url ? (
                  <a
                    href={detailQ.data.invoice.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-brand px-3 py-2 text-sm text-white hover:opacity-90"
                  >
                    Ver PDF
                  </a>
                ) : null}
                {canRepair && detailQ.data.invoice.status === 'authorized' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setResendFor(detailQ.data!.invoice);
                      setResendEmail('');
                    }}
                    data-help="invoices.resend"
                    className="rounded-lg border border-surface-border px-3 py-2 text-sm dark:border-surface-dark-border"
                  >
                    Reenviar por email
                  </button>
                ) : null}
                {canRepair &&
                detailQ.data.invoice.status !== 'authorized' &&
                detailQ.data.invoice.status !== 'rejected' ? (
                  <button
                    type="button"
                    onClick={() => setToRetry(detailQ.data!.invoice)}
                    data-help="invoices.retry"
                    className="rounded-lg border border-surface-border px-3 py-2 text-sm dark:border-surface-dark-border"
                  >
                    Reintentar emisión
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {resendFor ? (
        <section className="rounded-xl border border-surface-border p-4 dark:border-surface-dark-border">
          <h3 className="mb-2 text-sm font-semibold text-ink dark:text-ink-inverted">
            Reenviar {resendFor.full_number}
          </h3>
          <p className="mb-3 text-sm text-ink-muted">
            Sin dirección va al email registrado del usuario. La envía Facturante, con el
            comprobante oficial.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Email alternativo (opcional)">
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="otra@casilla.com"
                className={fieldCls({ extra: 'sm:w-72' })}
              />
            </Field>
            <button
              type="button"
              disabled={resendM.isPending}
              onClick={() =>
                resendM.mutate({
                  invoiceId: resendFor.invoice_id,
                  ...(resendEmail.trim() ? { email: resendEmail.trim() } : {}),
                })
              }
              className="rounded-lg bg-brand px-3 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              {resendM.isPending ? 'Enviando…' : 'Enviar'}
            </button>
            <button
              type="button"
              onClick={() => setResendFor(null)}
              className="rounded-lg border border-surface-border px-3 py-2 text-sm dark:border-surface-dark-border"
            >
              Cancelar
            </button>
          </div>
        </section>
      ) : null}

      <ConfirmDialog
        open={toRetry !== null}
        title="Reintentar emisión"
        description={
          <>
            Se vuelve a encolar la emisión del comprobante para el cobro{' '}
            <code className="font-mono">{toRetry?.payment_id.slice(0, 8)}…</code>. Si el
            comprobante ya existiera en Facturante, se recupera en vez de duplicarse.
          </>
        }
        confirmLabel="Reintentar"
        loading={retryM.isPending}
        onConfirm={() => {
          if (toRetry) retryM.mutate(toRetry.invoice_id);
        }}
        onCancel={() => setToRetry(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className={`text-ink dark:text-ink-inverted ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
